import type { Document } from "@contentful/rich-text-types";
import type { Entry } from "contentful";
import isNil from "lodash-es/isNil";
import { makeAutoObservable, runInAction } from "mobx";
import { PROMOTION_SECTION_SLUG } from "../../account/promotions/PromotionPage";
import { ensureError } from "../../common/errors";
import { logger } from "../../common/logger";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type {
   ContentEntryType,
   ContentfulArticle,
   ContentfulProductCategory,
   ContentfulSection,
   FocalPointData,
   ImageWithFocusData,
   MenuItem,
   SectionRecipeOrArticleOrCalendarContent,
   UrlData,
   WarningFields
} from "../../common/types/cmsTypes";
import type {
   TypeFooterSkeleton,
   TypeImageWithFocusSkeleton,
   TypeRecipeSkeleton,
   TypeV2_articleSkeleton
} from "../../common/types/contentful";
import { warnIfCyclicObject } from "../../common/utils";
import {
   type AsyncData,
   initializeWithDefaultData,
   isDataAvailable,
   isDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsLoading,
   setAsWaitingForData
} from "../../common/utils/asyncDataUtils";
import { setViewedPromotionArticlesInStorage } from "../../common/utils/storage/storageUtils";
import authStore from "../auth/authStore";
import featuresStore from "../features/featuresStore";
import {
   contentfulClient,
   createMenuItem,
   generateFullUrl,
   getAdventCalendarSlotSlugsAndParents,
   getArticleSlugsAndParents,
   getArticlesInSection,
   getFooterContent,
   getFullContent,
   getProductCategoryContent,
   getRecipeSlugsAndParents,
   getRecipesInSection,
   getSectionsSlugsAndParents,
   getUnprocessedUrlDataFromContent,
   getWarnings,
   PREVIEW_BASE_PATH,
   sortSectionContentEntries
} from "./contentUtils";
import segmentStore, { type SegmentedEntrySkeleton } from "./segmentStore";

const CONTRACTUAL_RECOMMENDATIONS_TAG = "contractualRecommendationsArticle";

// Used to cache requests for imageWithFocus resources
const cachedImageWithFocusPromise: Record<string, Promise<ImageWithFocusData | undefined>> = {};

type ContentStoreType = BaseStoreType & {
   urlData: AsyncData<UrlData[]>;
   // biome-ignore lint/suspicious/noExplicitAny: fetchedContent need some complex type that is not yet defined
   fetchedContent: Map<string, { type: ContentEntryType; entry: AsyncData<any> }>;
   sectionContent: {
      [id: string]: AsyncData<Entry<TypeV2_articleSkeleton | TypeRecipeSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>[]>;
   };
   sections: AsyncData<ContentfulSection[]>;
   productCategories: AsyncData<ContentfulProductCategory[]>;
   mainMenuItems: MenuItem[];
   contractualArticles: ContentfulArticle[];
   isPreviewMode: boolean;
   footerContent: AsyncData<Entry<TypeFooterSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string> | undefined>;
   allPromotionArticleIds: string[];
   viewedPromotionArticleIds: string[];

   fillStore: (location: string) => Promise<void>;
   fetchSectionContentForId: (sectionId: string) => Promise<void>;
   setSectionContent: (
      sectionId: string,
      content: AsyncData<Entry<TypeV2_articleSkeleton | TypeRecipeSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>[]>
   ) => void;
   getImageWithFocusUsingCache: (entryId: string) => Promise<ImageWithFocusData | undefined>;
   getImageWithFocus: (entryId: string) => Promise<ImageWithFocusData | undefined>;
   fetchContentIfNotAlreadyInStore: (id: string, type: ContentEntryType) => void;
   getRichTextForProductCategory: (path: string) => Document | null;
   getCurrentWarnings: () => WarningFields[];
   getSectionIdFromSlug: (slug: string) => string | undefined;
   getNewPromotionArticlesCount: () => number;
   addViewedPromotionArticleId: (promotionArticleId: string) => void;
};

const contentStore: ContentStoreType = makeAutoObservable({
   urlData: initializeWithDefaultData([]),
   fetchedContent: new Map(),
   sectionContent: {},
   sections: initializeWithDefaultData([]),
   productCategories: initializeWithDefaultData([]),
   mainMenuItems: [],
   contractualArticles: [],
   isPreviewMode: false,
   footerContent: initializeWithDefaultData(undefined),
   allPromotionArticleIds: [],
   viewedPromotionArticleIds: [],

   getCurrentWarnings: () => {
      const entriesArray = Array.from(contentStore.fetchedContent.entries());
      return entriesArray
         .filter((kv) => {
            const isWarning = kv[1].type === "warning";
            if (isWarning) {
               const visibleForCurrentSegment = segmentStore.isArticleVisible(kv[0]);
               return visibleForCurrentSegment;
            }
            return false;
         })
         .map((kv) => kv[1].entry.data);
   },

   /**
    * Why a separate cache wrapper method?
    * This method stores the promise generated the first time a call is made to fetch a specific
    * imageWithFocus. If multiple components call this method at the same time with the same entryId,
    * they will all get the same promise and it will be resolved with one request. If the same
    * entryId is requested later within the same session, the already-resolved promise is returned and
    * response reused without calling the API again.
    */
   getImageWithFocusUsingCache: (entryId): Promise<ImageWithFocusData | undefined> => {
      if (Object.hasOwn(cachedImageWithFocusPromise, entryId)) {
         return cachedImageWithFocusPromise[entryId];
      }

      const promise = contentStore.getImageWithFocus(entryId);
      cachedImageWithFocusPromise[entryId] = promise;

      return promise;
   },

   getImageWithFocus: async (entryId): Promise<ImageWithFocusData | undefined> => {
      const entry = await contentfulClient(
         contentStore.isPreviewMode
      ).withoutUnresolvableLinks.getEntry<TypeImageWithFocusSkeleton>(entryId);

      if (isNil(entry) || !entry.fields.image) {
         throw new Error("Image with focus either unavailable or does not contain image info");
      }

      const focalPoint = entry.fields.focalPoint as FocalPointData;

      return {
         entryId,
         altText: entry.fields.altText,
         focalPoint: focalPoint.focalPoint,
         originalAssetUrl: entry.fields.image.fields.file?.url
      };
   },

   fillStore: async (location) => {
      contentStore.isPreviewMode = location.split("/")[1] === PREVIEW_BASE_PATH;
      setAsWaitingForData(contentStore.urlData);
      setAsWaitingForData(contentStore.sections);
      setAsWaitingForData(contentStore.footerContent);
      setAsWaitingForData(contentStore.productCategories);

      try {
         const [
            sectionContent,
            articleContent,
            recipeContent,
            productCategoryContent,
            adventCalendarSlotContent,
            warningContent
         ] = await Promise.all([
            getSectionsSlugsAndParents(contentStore.isPreviewMode),
            getArticleSlugsAndParents(contentStore.isPreviewMode),
            getRecipeSlugsAndParents(contentStore.isPreviewMode),
            getProductCategoryContent(contentStore.isPreviewMode),
            getAdventCalendarSlotSlugsAndParents(contentStore.isPreviewMode),
            getWarnings(contentStore.isPreviewMode)
         ]);
         const segmentedEntries: Entry<SegmentedEntrySkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>[] = [
            ...articleContent,
            ...productCategoryContent,
            ...warningContent
         ];
         segmentStore.setArticleSegments(segmentedEntries);

         const sectionsRecipesAndArticles: SectionRecipeOrArticleOrCalendarContent[] = [
            ...sectionContent,
            ...articleContent,
            ...recipeContent,
            ...adventCalendarSlotContent
         ];

         const allContent = sectionsRecipesAndArticles.map(getUnprocessedUrlDataFromContent);
         const allUrlData = allContent.map((page) => generateFullUrl(page, allContent));

         runInAction(() => {
            contentStore.mainMenuItems = sectionsRecipesAndArticles.flatMap((content) => createMenuItem(content, allUrlData));

            contentStore.contractualArticles = articleContent.filter((article) =>
               article.metadata.tags.some((tag) => tag.sys.id === CONTRACTUAL_RECOMMENDATIONS_TAG)
            );

            // Initialize all possible keys for content fetching in the future
            for (const data of allUrlData) {
               contentStore.fetchedContent.set(data.id, { type: data.type, entry: initializeWithDefaultData({}) });
            }
            for (const warning of warningContent) {
               contentStore.fetchedContent.set(warning.sys.id, {
                  type: "warning",
                  entry: initializeWithDefaultData(warning.fields)
               });
            }
         });
         // Find content marked as main menu content

         const footerData = await getFooterContent(contentStore.isPreviewMode);
         setAsDataAvailable(contentStore.footerContent, footerData[0]);
         setAsDataAvailable(contentStore.productCategories, productCategoryContent);
         setAsDataAvailable(contentStore.sections, sectionContent);
         setAsDataAvailable(contentStore.urlData, allUrlData);
         const promotionSectionId = contentStore.getSectionIdFromSlug(PROMOTION_SECTION_SLUG);
         if (promotionSectionId) {
            await contentStore.fetchSectionContentForId(promotionSectionId);

            runInAction(() => {
               contentStore.allPromotionArticleIds = articleContent
                  .filter((article) => article.fields.visibleInSections?.includes(promotionSectionId))
                  .map((article) => article.sys.id);
            });
         }
      } catch (err) {
         logger.warn("contentful", "An error while building url data", err);
         const errorMsg = ensureError(err).message;
         setAsErrorOccured(contentStore.sections, errorMsg);
         setAsErrorOccured(contentStore.urlData, errorMsg);
      }
   },

   fetchSectionContentForId: async (sectionId) => {
      logger.log("contentful", `Fetching article list for section id ${sectionId}`);

      if (!contentStore.sectionContent[sectionId]) {
         contentStore.setSectionContent(sectionId, initializeWithDefaultData([]));
      }

      const sectionContent = contentStore.sectionContent[sectionId];
      setAsWaitingForData(sectionContent);

      try {
         const articles = await getArticlesInSection(sectionId, contentStore.isPreviewMode);
         const recipes = await getRecipesInSection(sectionId, contentStore.isPreviewMode);
         const content = [...sortSectionContentEntries(articles), ...sortSectionContentEntries(recipes)];

         setAsDataAvailable(sectionContent, content);
      } catch (err) {
         setAsErrorOccured(sectionContent, ensureError(err).message);
      }
   },

   setSectionContent(sectionId, content) {
      contentStore.sectionContent[sectionId] = content;
   },

   fetchContentIfNotAlreadyInStore: async (id: string, type: ContentEntryType) => {
      const content = contentStore.fetchedContent.get(id);
      if (!content || !isDefaultData(content.entry)) {
         return;
      }

      logger.log("contentful", `Starting full content fetch for id ${id} of type ${type}`);
      setAsLoading(content.entry);

      try {
         const result = await getFullContent(id, contentStore.isPreviewMode);
         warnIfCyclicObject(result);
         logger.log("contentful", `Full content results for ${id} (${type}):`, result);
         setAsDataAvailable(content.entry, result);
      } catch (err) {
         logger.warn("contentful", "Error during fetching full content", err);
         setAsErrorOccured(content.entry, ensureError(err).message);
      }
   },
   getRichTextForProductCategory: (path: string): Document | null => {
      if (isDataAvailable(contentStore.productCategories)) {
         const entries: ContentfulProductCategory[] = contentStore.productCategories.data;
         const filteredEntries = entries.filter((entry) => {
            return (
               (entry.fields?.categoryUrls?.includes(path) || entry.fields?.categoryPath === path) &&
               segmentStore.isArticleVisible(entry.sys.id)
            );
         });
         if (filteredEntries.length) {
            return filteredEntries[0].fields.body;
         }
      }
      return null;
   },
   getSectionIdFromSlug: (slug: string) => {
      const sections = contentStore?.sections?.data;
      if (!sections) {
         return;
      }
      const sectionId = contentStore.sections.data.find((section) => section.fields.slug === slug)?.sys.id;
      return sectionId;
   },
   getNewPromotionArticlesCount: () => {
      if (featuresStore.newPromotionNotificationsEnabled === false || !authStore.isLoggedIn()) {
         return 0;
      }
      const promotionId = contentStore.getSectionIdFromSlug(PROMOTION_SECTION_SLUG);
      if (!promotionId) {
         return 0;
      }
      const promotionArticlesCount = contentStore.allPromotionArticleIds.filter((articleId) =>
         segmentStore.isArticleVisible(articleId)
      ).length;
      const viewedPromotionArticleCount = contentStore.viewedPromotionArticleIds.length;
      const notViewedPromotionArticleCount = promotionArticlesCount - viewedPromotionArticleCount;
      return notViewedPromotionArticleCount;
   },
   addViewedPromotionArticleId: (promotionArticleId: string) => {
      if (contentStore.viewedPromotionArticleIds.includes(promotionArticleId)) {
         return;
      }
      const updatedViewedPromotionArticleIds = [...contentStore.viewedPromotionArticleIds, promotionArticleId];
      contentStore.viewedPromotionArticleIds = updatedViewedPromotionArticleIds;

      const companyId = authStore.currentCompany;
      if (companyId) {
         setViewedPromotionArticlesInStorage(companyId, updatedViewedPromotionArticleIds);
      }
   },
   clearCompanySpecificData: () => {
      // add methods to clear company specific data
   }
} satisfies ContentStoreType);

export default contentStore;
